import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import hostelService from '../../services/hostelService';
import DialogModal from '../../components/DialogModal';
import { FormSelect } from '../../components/finance';
import { MagnifyingGlass, Plus, PencilSimple, Trash, ArrowLeft } from '@phosphor-icons/react';
import './hostel.css';

function MaintenanceLogsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [maintenance, setMaintenance] = useState([]);
  const [filteredMaintenance, setFilteredMaintenance] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    hostel_id: '',
    room_id: '',
    maintenance_date: new Date().toISOString().split('T')[0],
    maintenance_type: 'repair',
    description: '',
    cost: 0,
    status: 'completed',
    notes: ''
  });

  const filterMaintenance = React.useCallback(() => {
    let filtered = maintenance;

    if (searchTerm) {
      filtered = filtered.filter(m =>
        m.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(m => m.status === statusFilter);
    }

    setFilteredMaintenance(filtered);
  }, [maintenance, searchTerm, statusFilter]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterMaintenance();
  }, [maintenance, searchTerm, statusFilter, filterMaintenance]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [maintenanceData, hostelsData, roomsData] = await Promise.all([
        hostelService.listMaintenance().catch(() => []),
        hostelService.listHostels().catch(() => []),
        hostelService.listRooms().catch(() => [])
      ]);
      
      const hostelsList = Array.isArray(hostelsData) ? hostelsData : hostelsData.hostels || [];
      const roomsList = Array.isArray(roomsData) ? roomsData : roomsData.rooms || [];
      
      setMaintenance(Array.isArray(maintenanceData) ? maintenanceData : maintenanceData.maintenance || []);
      setHostels(hostelsList);
      setRooms(roomsList);
    } catch (error) {
      console.error('Error loading maintenance logs:', error);
      toast.error('Failed to load maintenance logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.hostel_id || !formData.description || !formData.maintenance_date) {
        toast.error('Please fill in all required fields');
        return;
      }

      const submitData = {
        ...formData,
        cost: formData.cost ? parseFloat(formData.cost) : 0
      };

      if (editingId) {
        await hostelService.updateMaintenance(editingId, submitData);
        toast.success('Maintenance record updated successfully');
      } else {
        await hostelService.createMaintenance(submitData);
        toast.success('Maintenance record created successfully');
      }
      setShowModal(false);
      setEditingId(null);
      setFormData({
        hostel_id: '',
        room_id: '',
        maintenance_date: new Date().toISOString().split('T')[0],
        maintenance_type: 'repair',
        description: '',
        cost: 0,
        status: 'completed',
        notes: ''
      });
      loadData();
    } catch (error) {
      console.error('Error saving maintenance:', error);
      toast.error(editingId ? 'Failed to update maintenance' : 'Failed to create maintenance');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      hostel_id: item.hostel_id || '',
      room_id: item.room_id || '',
      maintenance_date: item.maintenance_date,
      maintenance_type: item.maintenance_type,
      description: item.description,
      cost: item.cost || 0,
      status: item.status,
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await hostelService.deleteMaintenance(deleteTarget);
      toast.success('Maintenance record deleted successfully');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting maintenance:', error);
      toast.error('Failed to delete maintenance record');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return <Layout><div className="loading">Loading maintenance logs...</div></Layout>;
  }

  const pending = maintenance.filter(m => m.status === 'pending').length;
  const completed = maintenance.filter(m => m.status === 'completed').length;

  return (
    <Layout>
      <div className="page hostel-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/hostel')} title="Back to Hostel">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Maintenance Logs</h1>
              <p>Track facility maintenance and repairs</p>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingId(null);
              setFormData({
                hostel_id: '',
                room_id: '',
                maintenance_date: new Date().toISOString().split('T')[0],
                maintenance_type: 'repair',
                description: '',
                cost: 0,
                status: 'completed',
                notes: ''
              });
              setShowModal(true);
            }}
          >
            <Plus size={20} />
            New Maintenance
          </button>
        </div>

        <div className="stats-bar">
          <div className="stat">
            <h3>Pending</h3>
            <p className="stat-value">{pending}</p>
          </div>
          <div className="stat">
            <h3>Completed</h3>
            <p className="stat-value">{completed}</p>
          </div>
          <div className="stat">
            <h3>Total</h3>
            <p className="stat-value">{maintenance.length}</p>
          </div>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search maintenance issues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="table-container">
          {filteredMaintenance.length === 0 ? (
            <div className="empty-state">
              <p>No maintenance logs found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Hostel</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Cost</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMaintenance.map((item) => (
                  <tr key={item.id}>
                    <td>{hostels.find(h => h.id === item.hostel_id)?.hostel_name || 'Unknown'}</td>
                    <td>{item.description}</td>
                    <td className="text-capitalize">{item.maintenance_type}</td>
                    <td>
                      <span className={`badge badge-${item.status === 'pending' ? 'warning' : item.status === 'completed' ? 'success' : 'secondary'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td>{item.maintenance_date}</td>
                    <td>GHS {item.cost?.toFixed(2) || '0.00'}</td>
                    <td className="action-buttons">
                      <button
                        className="btn-icon btn-primary"
                        title="Edit"
                        onClick={() => handleEdit(item)}
                      >
                        <PencilSimple size={18} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Delete"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Maintenance Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h2>{editingId ? 'Edit Maintenance' : 'New Maintenance'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <FormSelect
                      label="Hostel *"
                      name="hostel_id"
                      value={formData.hostel_id}
                      onChange={(e) => setFormData({ ...formData, hostel_id: e.target.value })}
                      required
                      options={hostels.map(h => ({
                        value: h.id,
                        label: h.hostel_name
                      }))}
                      placeholder="Select Hostel"
                    />
                  </div>
                  <div className="form-group">
                    <FormSelect
                      label="Room (Optional)"
                      name="room_id"
                      value={formData.room_id}
                      onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}
                      options={rooms.map(r => ({
                        value: r.id,
                        label: r.room_number
                      }))}
                      placeholder="Select Room"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Maintenance Date *</label>
                    <input
                      type="date"
                      value={formData.maintenance_date}
                      onChange={(e) => setFormData({ ...formData, maintenance_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <FormSelect
                      label="Type *"
                      name="maintenance_type"
                      value={formData.maintenance_type}
                      onChange={(e) => setFormData({ ...formData, maintenance_type: e.target.value })}
                      required
                      options={[
                        { value: 'repair', label: 'Repair' },
                        { value: 'maintenance', label: 'Maintenance' },
                        { value: 'cleaning', label: 'Cleaning' },
                        { value: 'inspection', label: 'Inspection' }
                      ]}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Fix broken sink in bathroom"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Cost</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-group">
                    <FormSelect
                      label="Status"
                      name="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      options={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'cancelled', label: 'Cancelled' }
                      ]}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingId ? 'Update' : 'Create'} Maintenance
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default MaintenanceLogsPage;
